import { Box, Card, CardActionArea, CardMedia } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useCreateRecipe, useGetRecipePosters } from '@/queries/useRecipes';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { log } from '@/logger/logger';
import { goToRecipe } from '@/utils/recipes';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { PlusIcon } from '@/UI/Icons/PlusIcon';
import type { SxProps, Theme } from '@mui/material/styles';

const WORKFLOW_CARDS =
  import.meta.env.MODE === 'development'
    ? [
        { id: 'dIjHiwG4WWVtodBraoA2', name: 'Generate from multiple models' },
        { id: 'jGJF0V3nxThNkXI6EIrB', name: 'Video Models' },
        { id: 'bJuFxnj3aSdoaLNCy8ix', name: 'Combining 2 styles' },
      ]
    : [
        { id: 'AyNcDuCYo9fjV4b3lMakHf', name: 'Multiple models' },
        { id: 'LcjGVMNKx4nbSgs7C4xHqo', name: 'Video compositor' },
        { id: 'yYmkpPNBfN4kHplL6FZgNA', name: 'Combining 2 styles' },
      ];

const WORKFLOW_IDS = WORKFLOW_CARDS.map((c) => c.id);

const CARD_SX: SxProps<Theme> = {
  width: 220,
  height: 150,
  backgroundImage: 'none',
  borderRadius: 2,
  border: '1.25px solid',
  borderColor: color.White08_T,
  boxShadow: 'none',
  position: 'relative',
};

const logger = log.getLogger('FigmaPromoWorkflowCards');

export function FigmaPromoWorkflowCards() {
  const { t } = useTranslation();
  const { data: posterMap = new Map<string, string | undefined>() } = useGetRecipePosters(WORKFLOW_IDS);
  const { mutateAsync: createRecipe } = useCreateRecipe();
  const { track } = useAnalytics();

  const handleWorkflowClick = (id: string, name: string) => {
    track('clicked_promo_workflow', { source: 'figma_promo_welcome', workflow: name }, TrackTypeEnum.BI);
    goToRecipe(id, true);
  };

  const handleStartFromScratch = async () => {
    track('created_new_flow', { source: 'figma_promo_welcome' }, TrackTypeEnum.BI);
    try {
      await createRecipe({ openInNewTab: true });
    } catch (error) {
      logger.error('error creating recipe from figma promo welcome', error);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        gap: 1.5,
        justifyContent: 'center',
        '@media (max-width: 1000px)': { flexWrap: 'wrap' },
      }}
    >
      {WORKFLOW_CARDS.map((card) => (
        <Card
          key={card.id}
          sx={{ ...CARD_SX, backgroundColor: 'transparent', overflow: 'hidden' }}
          onClick={() => handleWorkflowClick(card.id, card.name)}
        >
          <CardActionArea sx={{ height: '100%' }}>
            {posterMap.get(card.id) && (
              <CardMedia
                component="img"
                image={posterMap.get(card.id)}
                alt={card.name}
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0.8,
                background: `linear-gradient(0deg, ${color.Black100} 0%, rgba(107, 107, 120, 0) 100%)`,
              }}
            />
            <EllipsisText
              variant="body-sm-rg"
              maxWidth="190px"
              sx={{ position: 'absolute', bottom: 12, left: 16, color: color.White100 }}
            >
              {card.name}
            </EllipsisText>
          </CardActionArea>
        </Card>
      ))}

      <Card sx={{ ...CARD_SX, backgroundColor: color.White04_T }} onClick={() => void handleStartFromScratch()}>
        <CardActionArea sx={{ height: '100%' }}>
          <Box sx={{ position: 'absolute', top: 16, left: 16, color: color.White100 }}>
            <PlusIcon width={20} height={20} />
          </Box>
          <EllipsisText variant="body-sm-rg" maxWidth="190px" sx={{ position: 'absolute', bottom: 8, left: 16 }}>
            {t(I18N_KEYS.FIGMA_PROMO_WELCOME.START_FROM_SCRATCH)}
          </EllipsisText>
        </CardActionArea>
      </Card>
    </Box>
  );
}
